import { yupResolver } from "@hookform/resolvers/yup";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import pax8Hero from "assets/images/integration/pax8/pax8Hero.webp";
import pax8Logo from "assets/images/integration/pax8Logo.png";
import { Button, EyeCloseIcon, EyeOpenIcon, Input } from "components";
import { useConnectPax8 } from "controller/hooks/integration/usePax8";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import type { ConnectPax8Input } from "shared/types/integrations";
import { addMixpanelEvent, getApiErrorMessage } from "utilz/helpers";
import * as yup from "yup";

const schema: yup.ObjectSchema<ConnectPax8Input> = yup.object({
  client_id: yup.string().trim().required("Pax8 Client ID is required"),
  client_secret: yup.string().trim().required("Pax8 Client Secret is required"),
});

const INTEGRATIONS_PATH = "/app/control-center/integrations";
const PAX8_CONFIGURE_PATH = "/app/control-center/integrations/pax8/configure";

const Pax8Connect = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useLocation();
  const isUpdatingCredentials =
    (state as { mode?: string } | null)?.mode === "update_credentials";
  const [showSecret, setShowSecret] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ConnectPax8Input>({
    resolver: yupResolver(schema),
    defaultValues: { client_id: "", client_secret: "" },
  });

  const { mutateAsync: connectPax8, isLoading } = useConnectPax8();

  // Connection events count first-time connects only; Update Credentials
  // reuses this form and must not inflate them.
  const trackConnection = (
    event:
      | "pax8_credentials_submitted"
      | "pax8_connection_succeeded"
      | "pax8_connection_failed",
  ) => {
    if (!isUpdatingCredentials) addMixpanelEvent({ event });
  };

  const onSubmit = async (values: ConnectPax8Input) => {
    trackConnection("pax8_credentials_submitted");
    try {
      await connectPax8(values);
      trackConnection("pax8_connection_succeeded");
      toast.success(
        isUpdatingCredentials
          ? "Pax8 credentials updated"
          : "Pax8 connected successfully",
      );
      navigate(isUpdatingCredentials ? INTEGRATIONS_PATH : PAX8_CONFIGURE_PATH);
    } catch (error: unknown) {
      trackConnection("pax8_connection_failed");
      toast.error(
        getApiErrorMessage(
          error,
          isUpdatingCredentials
            ? "Couldn't update the Pax8 credentials"
            : "Failed to connect to Pax8",
        ),
      );
    }
  };

  return (
    <Stack direction="row" sx={{ height: "100%", overflow: "hidden" }}>
      <Stack
        flex={1}
        sx={{
          backgroundColor: theme.palette.neutral.main,
          alignItems: "center",
          justifyContent: "center",
          p: { xs: "32px 24px", sm: "60px 40px", md: "80px" },
        }}
      >
        <Stack sx={{ width: "100%", maxWidth: "472px", gap: "32px" }}>
          <Box
            component="img"
            src={pax8Logo}
            alt="Pax8 Logo"
            sx={{ width: "61px", height: "40px", objectFit: "contain" }}
          />

          <Typography
            variant="h5"
            sx={{
              color: theme.palette.lightBg.high,
              fontWeight: 700,
              lineHeight: 1.4,
            }}
          >
            Connect Pax8 with DeskDay to sync products, subscriptions, and
            automate billing.
          </Typography>

          <Stack
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ gap: "20px", width: "100%" }}
          >
            <Input
              name="client_id"
              control={control}
              label="Pax8 Client ID"
              placeholder="Enter your Pax8 client ID"
              error={!!errors.client_id}
              errorMessage={errors.client_id?.message}
            />

            <Input
              name="client_secret"
              control={control}
              label="Pax8 Client Secret"
              type={showSecret ? "text" : "password"}
              placeholder="xxxx xxxx xxxx xxxx"
              error={!!errors.client_secret}
              errorMessage={errors.client_secret?.message}
              InputProps={{
                endAdornment: (
                  <IconButton
                    type="button"
                    aria-label={showSecret ? "Hide secret" : "Show secret"}
                    onClick={() => setShowSecret((prev) => !prev)}
                    sx={{ color: theme.palette.lightBg.low }}
                  >
                    {showSecret ? <EyeCloseIcon /> : <EyeOpenIcon />}
                  </IconButton>
                ),
              }}
            />

            <Button
              type="submit"
              loading={isLoading}
              sx={{
                mt: "8px",
                width: "100%",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.neutral.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.hover,
                },
              }}
            >
              <span>
                {isUpdatingCredentials
                  ? "Update credentials"
                  : "Connect to Pax8"}
              </span>
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Stack
        flex={1}
        sx={{
          backgroundColor: theme.palette.common.black,
          overflow: "hidden",
          display: "flex",
          minHeight: "100%",
        }}
      >
        <Box
          component="img"
          src={pax8Hero}
          alt="Pax8 integration"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Stack>
    </Stack>
  );
};

export default Pax8Connect;
